import React, {Fragment, useCallback, useEffect, useReducer, useState} from "react";
import moment from "moment";

import Select from "react-select";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import AsyncSelect from "react-select/async";
import { useParams, Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import NumberFormat from "react-number-format";
import PhoneInput from "react-phone-number-input";
import { useHistory } from "react-router-dom";
import useRedirectIfVerified from "../../helpers/use-redirect-if-verified";
import { Datepicker, momentTimezone } from "@mobiscroll/react";
import isEqual from 'lodash.isequal';

import ai from "../../helpers/axios";
import timeFormatter from "../../helpers/timeFormatter";
import currencyFormatter from "../../helpers/currencyFormatter";

import ProspectsAutocomplete from "../prospects/prospects-autocomplete";
import AircraftAutocomplete from "../aircrafts/aircraft-autocomplete";

import TextInput from "../text-input";
import Loader from "../loader";

import { EditorState, ContentState, convertFromHTML } from "draft-js";

import { stateToHTML } from "draft-js-export-html";

import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import StateDisplay from "../reusable-modules/state-display";
import { LoadingButton } from "../shared/loading-button";
import { INVOICE_PAYMENT_TYPE_OPTIONS } from "../../helpers/format-payment-type";
import PageHeader from "../shared/page-header";
import UnsavedChangesWarningModal from "../unsaved-changes-warning-modal/unsaved-changes-warning-modal";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const invoiceTypesOptions = [
  {
    label: "Flight Charges",
    value: "flightCharges",
    notesRequired: true,
  },
  {
    label: "Catering",
    value: "catering",
    notesRequired: false,
  },
  {
    label: "Ground Transportation",
    value: "groundTransportation",
    notesRequired: false,
  },

  {
    label: "Cancellation Fee",
    value: "cancellationFee",
    notesRequired: false,
  },

  {
    label: "Refund",
    value: "refund",
    notesRequired: false,
  },
  {
    label: "Credit Card Processing Fee",
    value: "creditCardProcessingFee",
    notesRequired: false,
  },
  {
    label: "Other",
    value: "other",
    notesRequired: true,
  },
];

const copyObjectsArray = array => structuredClone(array);

const reducer = (state, action) => {
  switch (action.type) {
    case "addNewLeg": {
      return {
        ...state,
        legs: [
          ...state.legs,
          {
            departureDate: "",
            departureAirport: {
              airportName: "",
              id: "",
              code: "",
              city: "",
              localCode: "",
            },
            arrivalAirport: {
              airportName: "",
              id: "",
              code: "",
              city: "",
              localCode: "",
            },
            pax: "",
            aircraft: {
              name: "",
              seats: 8,
              yom: "",
              opi: "",
              id: "",
            },
          },
        ],
        legsDataChanged: true,
      };
    }
    case "deleteLeg": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray.splice(action.index, 1);
      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }
    case "changeLeg": {
      let newLegsArray = copyObjectsArray(state.legs);

      newLegsArray[action?.payload?.legIndex][action?.payload?.field] =
        action?.payload?.value;
      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }
    case "addNewInvoiceDetail": {
      return {
        ...state,
        legs: action.payload?.legs || state.legs,
        invoiceDetails: [
          ...state.invoiceDetails,
          {
            index: action.payload?.index || 0,
            typeLabel: action.payload?.typeLabel || { label: "", value: "" },
            description: action.payload?.description || "",
            amount: action.payload?.amount || "",
          },
        ],
        invoicesDataChanged: true,
      };
    }
    case "deleteInvoiceDetail": {
      let newDetailsArray = copyObjectsArray(state.invoiceDetails);
      newDetailsArray.splice(action.index, 1);
      return {
        ...state,
        invoiceDetails: newDetailsArray,
        invoicesDataChanged: true,
      };
    }
    case "changeInvoiceDetailField": {
      let invoiceDetailsArray = copyObjectsArray(state.invoiceDetails);
      invoiceDetailsArray[action?.payload?.invoiceDetailIndex][
        action?.payload?.field
      ] = action?.payload?.value;
      return {
        ...state,
        invoiceDetails: invoiceDetailsArray,
        invoicesDataChanged: true,
      };
    }
    case "addNewPaymentDetail": {
      return {
        ...state,
        paymentDetails: [
          ...state.paymentDetails,
          {
            date: "",
            typeLabel: { label: "", value: "" },
            notes: "",
            amount: "",
          },
        ],
        paymentDataChanged: true,
      };
    }
    case "changePaymentDetailField": {
      let newPaymentDetailsArray = copyObjectsArray(state.paymentDetails);
      newPaymentDetailsArray[action?.payload?.paymentDetailIndex][
        action?.payload?.field
      ] = action?.payload?.value;
      return {
        ...state,
        paymentDetails: newPaymentDetailsArray,
        paymentDataChanged: true,
      };
    }
    case "deletePaymentDetail": {
      let newPaymentDetailsArray = copyObjectsArray(state.paymentDetails);
      newPaymentDetailsArray.splice(action.index, 1);

      return {
        ...state,
        paymentDetails: newPaymentDetailsArray,
        paymentDataChanged: true,
      };
    }
    case "beginDataDownload": {
      return {
        ...state,
        loading: true,
      };
    }
    case "endDataDownload": {
      return {
        ...state,
        data: action?.payload?.data,
        loading: false,
        dataDownloadedTimestamp: Date.now(),
        operatorFinancialRecords:
          action?.payload?.data?.operatorFinancialRecords,
      };
    }
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
        ...(action.setToInitial && {
          initialFormValues: {
            ...state.initialFormValues,
            [action.field]: action.value,
          }
        })
      };
    }
    case "loadingChanged": {

      if (action.value === false) {
        return {
          ...state,
          isFormLoading: false,
          isSaveAndPreviewLoading: false,
          isSaveAndViewTripLoading: false,
        }
      }

      return {
        ...state,
        isFormLoading: true,
        [action.field]: action.value,
      }
    }
    case "unsavedChanges": {
      const updatedUnsavedChanges = {...state.unsavedChanges};

      if (!action.value) {
        delete updatedUnsavedChanges[action.field];
      } else {
        updatedUnsavedChanges[action.field] = true;
      }

      return {
        ...state,
        unsavedChanges: updatedUnsavedChanges
      };
    }
    default:
      return state;
  }
};

const resolveInvoicePaymentType = (typeValue) => {
  if (!typeValue) return null;
  let v = String(typeValue);
  if (v.toLowerCase() === "creditcard") v = "CC";
  if (v.toLowerCase() === "fundsonaccount") v = "Account";
  return (
    INVOICE_PAYMENT_TYPE_OPTIONS.find(
      (o) => o.value.toLowerCase() === v.toLowerCase()
    ) || { label: typeValue, value: typeValue }
  );
};

const isSamePaymentDay = (a, b) =>
  moment(a).isValid() && moment(b).isValid()
    ? moment(a).format("YYYY-MM-DD") === moment(b).format("YYYY-MM-DD")
    : a === b;

const sortMergedPaymentRows = (rows) =>
  [...rows].sort((a, b) => {
    const dateDiff = new Date(a.date) - new Date(b.date);
    if (dateDiff !== 0) return dateDiff;

    if (a.recordType !== b.recordType) {
      return a.recordType === "clientPaymentHistory" ? -1 : 1;
    }

    return 0;
  });

const buildLockedPaymentRows = (
  clientPaymentHistory = [],
  incidentalPayments = [],
  savedPaymentDetails = []
) => {
  const savedRecordType = (s) => s.recordType || "clientPaymentHistory";

  const tripPayments = (clientPaymentHistory || []).filter(
    (p) => p.paymentType !== "ccAuthorization" && !p.deleted
  );

  const savedById = new Map(
    (savedPaymentDetails || [])
      .filter((s) => savedRecordType(s) === "clientPaymentHistory" && s.clientPaymentId)
      .map((s) => [String(s.clientPaymentId), s])
  );

  const clientPaymentRows = tripPayments.map((payment) => {
    const id = String(payment._id);
    const saved = savedById.get(id);

    if (!saved) {
      return {
        recordType: "clientPaymentHistory",
        clientPaymentId: id,
        date: payment.paymentDate,
        typeLabel: resolveInvoicePaymentType(payment.paymentType),
        amount: payment.amount,
        notes: payment.notes || "",
        badge: "new",
      };
    }

    const modified =
      !isSamePaymentDay(payment.paymentDate, saved.date) ||
      String(payment.paymentType).toLowerCase() !==
        String(saved.typeLabel).toLowerCase() ||
      Number(payment.amount) !== Number(saved.amount);

    return {
      recordType: "clientPaymentHistory",
      clientPaymentId: id,
      date: payment.paymentDate,
      typeLabel: resolveInvoicePaymentType(payment.paymentType),
      amount: payment.amount,
      notes: saved.notes || "",
      badge: modified ? "modified" : null,
    };
  });

  const tripIds = new Set(tripPayments.map((p) => String(p._id)));
  const deletedClientPaymentRows = (savedPaymentDetails || [])
    .filter((s) => savedRecordType(s) === "clientPaymentHistory" && s.clientPaymentId && !tripIds.has(String(s.clientPaymentId)))
    .map((s) => ({
      recordType: "clientPaymentHistory",
      clientPaymentId: String(s.clientPaymentId),
      date: s.date,
      typeLabel: resolveInvoicePaymentType(s.typeLabel),
      amount: s.amount,
      notes: s.notes || "",
      badge: "deleted",
    }));

  const savedIncidentalsById = new Map(
    (savedPaymentDetails || [])
      .filter((s) => savedRecordType(s) === "incidentalCharges" && s.incidentalChargeId)
      .map((s) => [String(s.incidentalChargeId), s])
  );

  const incidentalRows = (incidentalPayments || [])
    .filter((payment) => payment.typeLabel)
    .map((payment) => {
      const id = String(payment.incidentalChargeId);
      const saved = savedIncidentalsById.get(id);

      return {
        recordType: "incidentalCharges",
        incidentalChargeId: id,
        date: payment.date,
        typeLabel: resolveInvoicePaymentType(payment.typeLabel),
        amount: payment.amount,
        notes: (saved ? saved.notes : payment.notes) || "",
        badge: saved ? null : "new",
      };
    });

  const incidentalIds = new Set((incidentalPayments || []).map((p) => String(p.incidentalChargeId)));
  const deletedIncidentalRows = (savedPaymentDetails || [])
    .filter((s) => savedRecordType(s) === "incidentalCharges" && s.incidentalChargeId && !incidentalIds.has(String(s.incidentalChargeId)))
    .map((s) => ({
      recordType: "incidentalCharges",
      incidentalChargeId: String(s.incidentalChargeId),
      date: s.date,
      typeLabel: resolveInvoicePaymentType(s.typeLabel),
      amount: s.amount,
      notes: s.notes || "",
      badge: "deleted",
    }));

  return sortMergedPaymentRows([
    ...clientPaymentRows,
    ...deletedClientPaymentRows,
    ...incidentalRows,
    ...deletedIncidentalRows,
  ]);
};

let invoiceNotesHTML = ` <h3>Please Note:</h3>
<p>Payment terms (deposits, schedules, methods, etc) related to this invoice may apply, and will be communicated elsewhere in writing by evoJets.</p>

<p>Please contact your evoJets representative with any questions.</p>

<p>Thank you for flying with evoJets!</p>`;

const invoiceNotesConverted = convertFromHTML(invoiceNotesHTML);

const defaultInvoiceNotesEditorState = EditorState.createWithContent(
  ContentState.createFromBlockArray(
    invoiceNotesConverted.contentBlocks,
    invoiceNotesConverted.entityMap
  )
);

const NewInvoiceContent = (props) => {
  const { tripId, invoiceId } = useParams();
  const history = useHistory();

  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();

  const [tripVerified, setTripVerified] = useState(false);
  const [tripLocked, setTripLocked] = useState(false);
  const superAdmin = (user?.["https://new.evojets.com/roles"] || []).some(
    (role) => role === "Super Admin",
  );
  const verificationAdmin = !!user?.["https://new.evojets.com/appMetadata"]?.isVerificationAdmin;
  useRedirectIfVerified(tripVerified, superAdmin, tripId, tripLocked, verificationAdmin);

  let initialState = {
    modalPhoneNumber: "",
    selectedClient: null,
    createProspectModalIsOpen: false,
    createAuthorizedSignerModalIsOpen: false,
    routingProcessed: false,
    prospectOptions: [],
    loading: true,
    useDifferentAuthorizedSigner: false,
    companyName: "",
    signerName: "",

    signerEmail: "",
    signerPhone: "",
    signerPhoneCountry: "US",

    authorizedSigners: [],

    paymentType: "",
    flightCharges: "",
    FET: "",
    ccFee: "",
    segmentFees: "",
    catering: "",
    groundTransportation: "",
    otherExpense: "",
    paymentApplied: "",
    discount: "",
    totalCharges: "",

    email: "",
    emailInputValue: "",
    subject: "",

    cc: [],
    ccInputValue: "",
    legs: [],
    invoiceDetails: [],
    paymentDetails: [],
    paymentsLocked: false,
    data: null,
    dataDownloadedTimestamp: null,
    invoiceNotesEditor: defaultInvoiceNotesEditorState,

    isFormLoading: false,
    isSaveAndPreviewLoading: false,
    isSaveAndViewTripLoading: false,
    tripTag: "",

    legsDataChanged: false,
    invoicesDataChanged: false,
    paymentDataChanged: false,
    initialFormValues: {
      legs: [],
      invoiceDetails: [],
      paymentDetails: [],
      invoiceNotesEditor: defaultInvoiceNotesEditorState,
    },
    unsavedChanges: {},
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();

    if (state.isFormLoading) {
      return;
    }

    const isSaveAndPreviewClick = e.target.id === "preview";

    const token = await getAccessTokenSilently().catch((e) => {
      return loginWithRedirect();
    });

    const legsErrorFields = [];

    state.legs.map((leg) => {
      [
        {name: 'departureDate', label: 'Date'},
        {name: 'departureAirport', label: 'Depart'},
        {name: 'arrivalAirport', label: 'Arrive'},
        {name: 'pax', label: 'PAX'},
        {name: 'aircraft', label: 'Aircraft'},
        {name: 'ete', label: 'ETE'}
      ].forEach(({name, label}) => {
        const field = leg[name];

        if (
          [null, undefined].includes(field)
          || Object.prototype.toString.call(field) === "[object Date]" && !moment(field).isValid()
          || typeof field === 'string' && field.trim() === ""
          || typeof field === 'object' &&
          (Object.prototype.toString.call(field) === "[object Date]"
            ? !moment(field).isValid()
            : !field?.id?.length)
        ) {
          legsErrorFields.push(label);
        }
      });
    });

    if (legsErrorFields.length) {
      toast.error(
        `Please fill in ${[...new Set(legsErrorFields)].join(', ')} for each leg under Trip Summary`,
        toastOptions
      );
      return;
    }

    let stopEarly = false;

    const invoiceDetailsErrorFields = [];

    let invoiceDetails =
      state?.invoiceDetails
        ?.map((detail) => {
          const matchingOption = invoiceTypesOptions.find(
            (option) => option.value === detail.typeLabel?.value
          );

          // Check if notes are required and if description is blank
          if (matchingOption?.notesRequired && !detail.description?.trim()) {
            toast.error(
              `Please enter a description for all "Flight Charges" and "Other" line items under Invoice Details`,
              toastOptions
            );
            stopEarly = true;
          }

          if (!detail.typeLabel?.value) {
            invoiceDetailsErrorFields.push('Type')
          }

          if ([null, undefined].includes(detail.amount) || detail.amount === "") {
            invoiceDetailsErrorFields.push('Amount');
          }

          return {
            typeLabel: detail.typeLabel?.value,
            description: detail.description || " ", // Ensures description is never empty
            amount: detail.amount,
          };
        })
        .filter((detail) => detail.typeLabel && detail.amount) || [];

    if (stopEarly) {
      return;
    }

    if (invoiceDetailsErrorFields.length) {
      toast.error(
        `Please fill in ${[...new Set(invoiceDetailsErrorFields)].join(', ')} for each detail under Invoice Details`,
        toastOptions
      );
      return;
    }

    const paymentDetailsErrorFields = [];

    (state.paymentsLocked ? [] : state.paymentDetails).map((detail) => {
      [
        {name: 'date', label: 'Date'},
        {name: 'typeLabel', label: 'Type'},
        {name: 'amount', label: 'Amount'},
      ].forEach(({name, label}) => {
        const field = detail[name];

        if (
          [null, undefined].includes(field)
          || Object.prototype.toString.call(field) === "[object Date]" && !moment(field).isValid()
          || typeof field === 'string' && field.trim() === ""
          || typeof field === 'object' &&
          (Object.prototype.toString.call(field) === "[object Date]"
            ? !moment(field).isValid()
            : !field?.value?.length)
        ) {
          paymentDetailsErrorFields.push(label);
        }
      });
    });

    if (paymentDetailsErrorFields.length) {
      toast.error(
        `Please fill in ${[...new Set(paymentDetailsErrorFields)].join(', ')} for each detail under Payment Details`,
        toastOptions
      );
      return;
    }

    let invoiceNotes = stateToHTML(
      state.invoiceNotesEditor.getCurrentContent()
    );

    const invoiceData = {
      signerName: state.signerName,
      signerEmail: state.signerEmail,
      signerPhone: state.signerPhone,
      signerPhoneCountry: state.signerPhoneCountry,
      companyName: state.companyName,
      tripId: tripId,
      clientId: state?.selectedClient?.value,

      invoiceDetails,
      paymentDetails:
        state?.paymentDetails
          ?.filter(
            (detail) => !(state.paymentsLocked && detail.badge === "deleted")
          )
          ?.map((detail) => ({
            ...(detail.recordType === "incidentalCharges"
              ? { recordType: "incidentalCharges", incidentalChargeId: detail.incidentalChargeId }
              : detail.clientPaymentId
                ? { clientPaymentId: detail.clientPaymentId }
                : {}),
            date: detail.date,
            typeLabel: detail.typeLabel?.value,
            notes: detail.notes,
            amount: detail.amount,
          }))
          .filter(
            (detail) => detail.date && detail.amount && detail.typeLabel
          ) || [],

      // TODO: Add all legs when we will have airports data from the API
      tripSummary:
        state.legs.map((leg) => ({
          departureAirport: leg?.departureAirport || "",
          arrivalAirport: leg?.arrivalAirport || "",
          date: leg.departureDate,
          ete: leg?.ete || "",
          pax: leg.pax,
          aircraft: {
            id: leg.aircraft.value,
            name: leg.aircraft.label,
          },
          distance: leg.distance,
        })) || [],
      notes: invoiceNotes,
    };

    dispatch({
      type: "loadingChanged",
      field: isSaveAndPreviewClick ? "isSaveAndPreviewLoading" : "isSaveAndViewTripLoading",
      value: true,
    });

    let response;

    try {
      if (!invoiceId || invoiceId === "new") {
        if (tripId) {
          const tripCheck = await ai
            .auth(token)
            .get(`/api/trips/${tripId}`)
            .catch((error) => {
              console.error(error);
              Sentry.captureException(error);
            });

          if (tripCheck?.data?.invoiceData?.length) {
            toast.error("Invoice already exists for this Trip.", toastOptions);
            return;
          }

          response = await ai
            .auth(token)
            .post(`/api/invoices/create/`, invoiceData)
            .catch((error) => {
              console.error(error);
              Sentry.captureException(error);
              toast.error(
                error?.response?.data?.message ||
                  "Something went wrong! Please try again later!",
                toastOptions
              );
            });
        }
      } else {
        response = await ai
          .auth(token)
          .put(`/api/invoices/${invoiceId}`, invoiceData)
          .catch((error) => {
            console.error(error);
            Sentry.captureException(error);
          });
      }
    } finally {
      dispatch({
        type: "loadingChanged",
        field: "isFormLoading",
        value: false,
      });
    }

    if (!response || !response.data) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "unsavedChanges",
      value: {},
    });

    setTimeout(() => {
      toast.success("Invoice saved successfully!", toastOptions);
    }, 0);

    let path = `/trips/${tripId}${isSaveAndPreviewClick ? `/invoices/${response.data._id}/preview` : ''}`;

    history.push(path);
  };

  const handleClientOptionCreation = (inputValue) => {
    dispatch({
      type: "fieldChanged",
      field: "createProspectModalIsOpen",
      value: true,
    });
  };

  const handleClientChange = (selectedClient) => {
    setUnsavedChanges({
      field: "selectedClient",
      value: selectedClient?._id !== state.initialFormValues.selectedClient?._id,
    });

    if (selectedClient) {
      selectedClient._id = selectedClient?.value;

      // Dispatch the selected client
      dispatch({
        type: "fieldChanged",
        field: "selectedClient",
        value: selectedClient,
      });

      // Dispatch signerPhone (use the first phone number in the phoneNumbers array if it exists, otherwise fallback to phone)
      const signerPhone =
        selectedClient.phoneNumbers?.[0]?.number || selectedClient.phone;
      dispatch({
        type: "fieldChanged",
        field: "signerPhone",
        value: signerPhone,
      });

      // Dispatch signerName (combine first and last name)
      const signerName = `${selectedClient.firstName} ${selectedClient.lastName}`;
      dispatch({
        type: "fieldChanged",
        field: "signerName",
        value: signerName,
      });

      // Dispatch signerPhoneCountry (use the first phone number's country code if it exists, otherwise fallback to countryCode)
      const signerPhoneCountry =
        selectedClient.phoneNumbers?.[0]?.countryCode ||
        selectedClient.countryCode;
      dispatch({
        type: "fieldChanged",
        field: "signerPhoneCountry",
        value: signerPhoneCountry,
      });

      // Dispatch signerEmail (use the email from selectedClient)
      dispatch({
        type: "fieldChanged",
        field: "signerEmail",
        value: selectedClient.email,
      });

      // Dispatch companyName if it exists
      dispatch({
        type: "fieldChanged",
        field: "companyName",
        value: selectedClient.companyName || "",
      });
    } else {
      dispatch({
        type: "fieldChanged",
        field: "selectedClient",
        value: selectedClient,
      });
    }
  };

  const handleTextInputChange = (e) => {
    setUnsavedChanges({
      field: e.target.name,
      value: e.target.value !== state.initialFormValues[e.target.name],
    });

    return dispatch({
      type: "fieldChanged",
      field: e.target.name,
      value: e.target.value,
    });
  };

  const handleAddLeg = (event) => {
    event.preventDefault();

    dispatch({ type: "addNewLeg" });
  };

  const handleAddDetail = (event) => {
    event.preventDefault();
    dispatch({ type: "addNewInvoiceDetail" });
  };

  const handleAddPaymentDetail = (event) => {
    event.preventDefault();

    dispatch({ type: "addNewPaymentDetail" });
  };

  const handleAircraftChange = async (legIndex, updatedAircraft, action) => {
    const legData = { ...state.legs[legIndex] };

    if (updatedAircraft) {
      const processedAircraft = {
        id: updatedAircraft.value,
        name: updatedAircraft.label,
        value: updatedAircraft.value,
        label: updatedAircraft.label,
      };

      legData.aircraft = processedAircraft;

      dispatch({
        type: "changeLeg",
        payload: {
          field: "aircraft",
          legIndex: legIndex,
          value: processedAircraft,
        },
      });
    } else {
      dispatch({
        type: "changeLeg",
        payload: {
          field: "aircraft",
          legIndex: legIndex,
          value: updatedAircraft,
        },
      });
    }

    //need to recalculate flight times

    await handleFlightTimeCalculationByIndex(legData, legIndex);
  };

  const handleETEChange = async (legIndex, updatedETE) => {
    let leg = { ...state.legs[legIndex] };

    let pattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

    const isValid = pattern.test(updatedETE);

    if (!isValid) {
      dispatch({
        type: "changeLeg",
        payload: {
          legIndex: legIndex,
          field: "ete",
          value: "",
        },
      });

      return toast.error(
        `Please enter a valid ETE for Leg ${legIndex + 1}`,
        toastOptions
      );
    }
  };

  const handleFlightTimeCalculationMulti = async (legData) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let params = {
      legData: legData.map((leg, index) => {
        return {
          arrivalAirportId: leg.arrivalAirport.id,
          departureAirportId: leg.departureAirport.id,
          aircraftId: leg.aircraft?.id,
          legIndex: index,
        };
      }),
    };

    let response = await ai
      .auth(token)
      .post(`/api/trips/flightTimesMulti`, params)
      .catch((err) => {
        console.error(err.response.data);
        toast.error(
          "Please fill out all required fields: ensure an aircraft is selected for this Aircraft Option, and departure and arrival airports for all legs.",
          toastOptions
        );
        return;
      });

    if (response && response?.data && response?.data?.length) {
      let distanceResponse = response.data.map((leg) => {
        return {
          distance: leg.value.distance,
        };
      });

      return distanceResponse;
    }
  };

  const handleFlightTimeCalculationByIndex = async (legData, legIndex) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    if (
      !legData?.arrivalAirport?.id ||
      !legData?.departureAirport?.id ||
      !legData?.aircraft?.id
    ) {
      return;
    }

    let params = {
      legData: {
        arrivalAirportId: legData.arrivalAirport.id,
        departureAirportId: legData.departureAirport.id,
        legIndex: legIndex,
        aircraftId: legData.aircraft.id,
      },
    };

    let response = await ai
      .auth(token)
      .post(`/api/trips/flightTimes`, params)
      .catch((err) => {
        console.error(err.response.data);
        toast.error(
          "Please fill out all required fields: ensure an aircraft is selected for this Aircraft Option, and departure and arrival airports for all legs.",
          toastOptions
        );
        return;
      });

    if (
      response &&
      response?.data &&
      response?.data?.length &&
      response?.data[0]?.value
    ) {
      let returnedValueForLeg = response?.data[0]?.value;

      let composedString = "";

      if (returnedValueForLeg?.timeWithWindAdjustment) {
        composedString = timeFormatter(
          returnedValueForLeg?.timeWithWindAdjustment
        );
      }

      dispatch({
        type: "changeLeg",
        payload: {
          legIndex,
          field: "ete",
          value: composedString,
        },
      });

      dispatch({
        type: "changeLeg",
        payload: {
          legIndex,
          field: "distance",
          value: returnedValueForLeg.distance,
        },
      });

      return response.data;
    }
  };

  const handleAirportChange = async (legIndex, airport, airportIndex) => {
    let legData = { ...state.legs[legIndex] };

    if (airportIndex === 0) {
      legData.departureAirport = {
        id: airport.value,
        name: airport.label,
      };

      let departureTimeZone = null;
      if (airport.tz && airport.tz.length) {
        departureTimeZone = airport.tz[0];
      }

      if (departureTimeZone?.length) {
        dispatch({
          type: "changeLeg",
          payload: {
            legIndex: legIndex,
            field: "departureTimeZone",
            value: departureTimeZone,
          },
        });
      }
    }

    if (airportIndex === 1) {
      legData.arrivalAirport = {
        id: airport.value,
        name: airport.label,
      };

      let arrivalTimeZone = null;

      if (airport.tz && airport.tz.length) {
        arrivalTimeZone = airport.tz[0];
      }

      if (arrivalTimeZone?.length) {
        dispatch({
          type: "changeLeg",
          payload: {
            legIndex: legIndex,
            field: "arrivalTimeZone",
            value: arrivalTimeZone,
          },
        });
      }
    }

    await handleFlightTimeCalculationByIndex(legData, legIndex);
  };

  const loadAirportOptions = async (inputValue) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });
    let { data } = await ai
      .auth(token)
      .get(`/api/airports/search?q=${inputValue}&includeTZ=true`);

    let options = [];
    if (data.length >= 1) {
      options = data.map((i) => ({
        value: i._id,
        label: i.airportName,
        code: i.code,
        city: i.city,
        localCode: i.localCode,
        lat: i.lat,
        lon: i.lon,
        tz: i.tz,
      }));
    }

    return options;
  };

  const setUnsavedChanges = useCallback(({field, value}) => {
    dispatch({
      type: "unsavedChanges",
      field,
      value
    })
  }, []);

  //extract logic out to two functions

  const prepareEditForm = (selectedInvoice, tripDetails) => {
    // Parse the client data and pre-fill the fields
    if (selectedInvoice?.clientData) {
      parseClientData(selectedInvoice.clientData);
    }

    const paymentsLocked = !!selectedInvoice?.paymentsLocked;
    dispatch({
      type: "fieldChanged",
      field: "paymentsLocked",
      value: paymentsLocked,
    });

    // Pre-fill invoice details
    dispatch({
      type: "fieldChanged",
      field: "invoiceDetails",
      value: selectedInvoice?.invoiceDetails?.map((detail) => ({
        ...detail,
        typeLabel: invoiceTypesOptions.find(
          (option) => option.value === detail.typeLabel
        ),
      })),
    });

    // Pre-fill trip summary
    if (selectedInvoice?.tripSummary?.length) {
      dispatch({
        type: "fieldChanged",
        field: "legs",
        value: selectedInvoice.tripSummary.map((summary) => ({
          ...summary,
          pax: summary.pax, // Ensure PAX is pre-filled
          aircraft: {
            name: summary.aircraft?.name || "",
            id: summary.aircraft?.id || "",
            value: summary.aircraft?.id || "",
            label: summary.aircraft?.name || "",
          },
          departureDate: summary.date,
          departureAirport: {
            airportName: summary.departureAirport?.airportName || "",
            id: summary.departureAirport?.id || "",
            code: summary.departureAirport?.code || "",
            city: summary.departureAirport?.city || "",
            localCode: summary.departureAirport?.localCode || "",
          },
          arrivalAirport: {
            airportName: summary.arrivalAirport?.airportName || "",
            id: summary.arrivalAirport?.id || "",
            code: summary.arrivalAirport?.code || "",
            city: summary.arrivalAirport?.city || "",
            localCode: summary.arrivalAirport?.localCode || "",
          },
        })),
        setToInitial: true,
      });
    }

    // Pre-fill payment details
    if (paymentsLocked) {
      dispatch({
        type: "fieldChanged",
        field: "paymentDetails",
        value: buildLockedPaymentRows(
          tripDetails?.data?.clientPaymentHistory,
          tripDetails?.data?.invoiceIncidentalPayments,
          selectedInvoice?.paymentDetails
        ),
        setToInitial: true,
      });
    } else {
      dispatch({
        type: "fieldChanged",
        field: "paymentDetails",
        value: selectedInvoice?.paymentDetails?.map((payment) => {
          if (payment.typeLabel.toLowerCase() === "creditcard") {
            payment.typeLabel = "CC";
          }
          if (payment.typeLabel.toLowerCase() === "fundsonaccount") {
            payment.typeLabel = "Account";
          }
          const matchedTypeLabel = INVOICE_PAYMENT_TYPE_OPTIONS.find((option) => {
            return option.value.toLowerCase() === payment.typeLabel.toLowerCase();
          });

          return {
            ...payment,
            typeLabel: matchedTypeLabel || null, // Set to null if not matched to avoid issues
          };
        }),
        setToInitial: true,
      });
    }
    // Handle signer details if not already set
    if (!state.signerName && selectedInvoice.signerName) {
      dispatch({
        type: "fieldChanged",
        field: "signerName",
        value: selectedInvoice.signerName,
        setToInitial: true,
      });
    }

    if (!state.signerEmail && selectedInvoice.signerEmail) {
      dispatch({
        type: "fieldChanged",
        field: "signerEmail",
        value: selectedInvoice.signerEmail,
        setToInitial: true,
      });
    }

    if (!state.signerPhone && selectedInvoice.signerPhone) {
      dispatch({
        type: "fieldChanged",
        field: "signerPhone",
        value: selectedInvoice.signerPhone,
        setToInitial: true,
      });
    }

    // If there is a company name in the invoice data, pre-fill it
    if (selectedInvoice.companyName) {
      dispatch({
        type: "fieldChanged",
        field: "companyName",
        value: selectedInvoice.companyName,
        setToInitial: true,
      });
    }

    if (selectedInvoice?.notes) {
      let invoiceNotes = convertFromHTML(selectedInvoice.notes);

      let invoiceNotesEditorState = EditorState.createWithContent(
        ContentState.createFromBlockArray(
          invoiceNotes.contentBlocks,
          invoiceNotes.entityMap
        )
      );

      dispatch({
        type: "fieldChanged",
        field: "invoiceNotesEditor",
        value: invoiceNotesEditorState,
        setToInitial: true,
      });
    }

    if (selectedInvoice.clientData && selectedInvoice?.clientData?.length) {
      let i = selectedInvoice.clientData[0];
      let label = `${i?.firstAndLastName} - ${i.emails[0]}`;

      if (!i?.firstAndLastName) {
        if (!i.firstName) {
          i.firstName = "";
        }

        if (!i.lastName) {
          i.lastName = "";
        }

        label = `${i.firstName} ${i.lastName} - ${i.emails[0]}`;
      }

      if (i.companyName) {
        label = `${label} - ${i.companyName}`;
      }

      if (i.assigneeName && i.assigneeId) {
        label = `${label} - ${i.assigneeName}`;
      }

      i.label = label;

      dispatch({
        type: "fieldChanged",
        field: "selectedClient",
        value: i,
        setToInitial: true,
      });
    }
  };

  const prepareNewInvoice = async (tripDetails) => {
    //get all signed contracts
    const signedContracts = tripDetails?.data?.contractData?.filter(
      (contract) => contract.stage === "signed"
    );

    //create invoice details array to seed flight charges correctly
    const invoiceDetailsArray = signedContracts.reduce((acc, contract) => {
      // Check if legs array exists and has at least one leg, and if flightCharges is defined
      if (
        contract.legs &&
        contract.legs.length > 0 &&
        contract.legs[0].aircraft &&
        contract.pricing.flightCharges !== undefined
      ) {
        acc.push({
          typeLabel: {
            label: "Flight Charges",
            value: "flightCharges",
          },
          description: contract.legs[0].aircraft.name,
          amount: contract.pricing.flightCharges,
        });
      }

      return acc;
    }, []);

    //update invoice details in state
    dispatch({
      type: "fieldChanged",
      field: "invoiceDetails",
      value: invoiceDetailsArray,
      setToInitial: true,
    });

    //handle client payment history

    const paymentHistoryArray = tripDetails?.data?.clientPaymentHistory
      ?.filter(
        ({ paymentType, deleted }) =>
          paymentType !== "ccAuthorization" && !deleted
      )
      ?.map((payment) => {
        return {
          recordType: "clientPaymentHistory",
          clientPaymentId: String(payment._id),
          date: payment.paymentDate,
          amount: payment.amount,
          typeLabel: INVOICE_PAYMENT_TYPE_OPTIONS.find(({ value }) => value.toLowerCase() === payment.paymentType.toLowerCase()) || {
            label: payment.paymentType,
            value: payment.paymentType,
          },
          notes: payment.notes,
          badge: null,
        };
      }) || [];

    const incidentalPaymentsArray = (tripDetails?.data?.invoiceIncidentalPayments || [])
      .filter((payment) => payment.typeLabel)
      .map((payment) => ({
        recordType: "incidentalCharges",
        incidentalChargeId: String(payment.incidentalChargeId),
        date: payment.date,
        amount: payment.amount,
        typeLabel: resolveInvoicePaymentType(payment.typeLabel),
        notes: payment.notes || "",
        badge: null,
      }));

    //update payment details  in state
    dispatch({
      type: "fieldChanged",
      field: "paymentDetails",
      value: sortMergedPaymentRows([...paymentHistoryArray, ...incidentalPaymentsArray]),
      setToInitial: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "paymentsLocked",
      value: true,
    });

    if (tripDetails) {
      dispatch({
        type: "endDataDownload",
        payload: {
          data: tripDetails.data,
        },
      });

      const legsSourceContract = signedContracts?.[0] || tripDetails.data.contractData[0];
      let processedLegs = legsSourceContract?.legs || [];

      if (processedLegs.length) {
        processedLegs = processedLegs.map((leg) => ({
          ...leg,
          pax: (leg.passengers || leg.pax) ?? "",
          ...(leg.aircraft && {
            aircraft: {
              ...leg.aircraft,
              value: leg.aircraft.id,
              label: leg.aircraft.name,
            },
          })
        }));

        let distanceResponse = await handleFlightTimeCalculationMulti(
          processedLegs
        );

        // Add distance values to the processed legs
        processedLegs = processedLegs.map((leg, index) => {
          return {
            ...leg,
            distance: distanceResponse?.[index]?.distance || 0, // Ensure distance is directly assigned
          };
        });
      }

      dispatch({
        type: "fieldChanged",
        field: "legs",
        value: processedLegs,
        setToInitial: true,
      });

      parseClientData(tripDetails.data?.clientData[0]);
    }
  };

  const prepareTripAndInvoiceData = async ({ tripId, invoiceId }) => {
    dispatch({ type: "beginDataDownload" });

    const token = await getAccessTokenSilently().catch((e) => {
      return loginWithRedirect();
    });

    const tripDetails = await ai
      .auth(token)
      .get(`/api/trips/${tripId}`)
      .catch((error) => {
        setTimeout(() => {
          toast.error("Something went wrong! Please try again later!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }, 50);
      });

    setTripVerified(!!tripDetails?.data?.verified);
    setTripLocked(!!tripDetails?.data?.lockState?.locked);

    if (!invoiceId || invoiceId === "new") {
      //if the invoice is new

      prepareNewInvoice(tripDetails);
    }

    let selectedInvoice;
    if (invoiceId && invoiceId !== "new") {
      //existing invoice, get only the current invoice by ID
      let invoiceResponse = await ai
        .auth(token)
        .get(`/api/invoices/${invoiceId}`);

      if (
        invoiceResponse &&
        invoiceResponse.data &&
        invoiceResponse.data.invoice
      ) {
        selectedInvoice = invoiceResponse.data.invoice;
      }
    }

    if (selectedInvoice) {
      // Invoice is not new, so we prepare the form with the existing data
      prepareEditForm(selectedInvoice, tripDetails);

      if (tripDetails) {
        dispatch({
          type: "endDataDownload",
          payload: {
            data: tripDetails.data,
          },
        });
      }
    }
  };

  const parseClientData = (clientData) => {
    if (!clientData) {
      return false;
    }

    if (!clientData?._id || !clientData?.firstName || !clientData?.lastName) {
      //no contact info
      return false;
    }

    if (clientData?.isBusiness && !clientData?.companyName) {
      //client is business but missing company name
      return false;
    }

    let label = `${clientData.firstName} ${clientData?.lastName}`;
    let value = clientData?._id;

    if (clientData?.isBusiness) {
      label = clientData.companyName;

      dispatch({
        type: "fieldChanged",
        field: "companyName",
        value: clientData.companyName,
        setToInitial: true,
      });
    }

    if (clientData?.firstName && clientData?.lastName) {
      dispatch({
        type: "fieldChanged",
        field: "signerName",
        value: `${clientData.firstName} ${clientData?.lastName}`,
        setToInitial: true,
      });
    }

    if (clientData?.phoneNumbers?.length) {
      if (
        clientData?.phoneNumbers[0]?.number &&
        clientData?.phoneNumbers[0]?.countryCode
      ) {
        dispatch({
          type: "fieldChanged",
          field: "signerPhone",
          value: clientData?.phoneNumbers[0]?.number,
          setToInitial: true,
        });
        dispatch({
          type: "fieldChanged",
          field: "signerPhoneCountry",
          value: clientData?.phoneNumbers[0]?.countryCode,
          setToInitial: true,
        });
      }
    } else if (clientData?.workPhones?.length) {
      dispatch({
        type: "fieldChanged",
        field: "signerPhone",
        value: clientData?.workPhones[0],
        setToInitial: true,
      });
    } else if (clientData?.mobilePhones?.length) {
      dispatch({
        type: "fieldChanged",
        field: "signerPhone",
        value: clientData?.mobilePhones[0],
        setToInitial: true,
      });
    }

    parseAuthorizedSigners(clientData);

    if (clientData?.emails?.length) {
      dispatch({
        type: "fieldChanged",
        field: "email",
        value: clientData.emails[0],
        setToInitial: true,
      });

      dispatch({
        type: "fieldChanged",
        field: "signerEmail",
        value: clientData.emails[0],
        setToInitial: true,
      });
    }

    dispatch({
      type: "fieldChanged",
      field: "selectedClient",
      value: {
        label,
        value,
        ...clientData,
      },
      setToInitial: true,
    });
  };

  const parseAuthorizedSigners = (clientData) => {
    if (!clientData?.additionalUsers?.length) {
      return false;
    }

    let authorizedSigners = clientData.additionalUsers.flatMap((item) => {
      if (!item?.isAuthorizedSigner) {
        return [];
      }
      let label = `${item.firstName} ${item.lastName} - ${item.relationship}`;
      let value = item?._id;
      return {
        ...item,
        label,
        value,
      };
    });

    dispatch({
      type: "fieldChanged",
      field: "authorizedSigners",
      value: authorizedSigners,
      setToInitial: true,
    });
  };

  useEffect(() => {
    prepareTripAndInvoiceData({
      invoiceId,
      tripId,
    });

    document.title = "Trip Invoice | evoJets HQ";

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, tripId]);

  useEffect(() => {
    if (state.legsDataChanged) {
      // nuance with fields
      // date
      // depart airport
      // arrive airport

      setUnsavedChanges({
        field: "legs",
        value: !isEqual(state.legs, state.initialFormValues.legs)
      });

      dispatch({
        type: "fieldChanged",
        field: "legsDataChanged",
        value: false,
      });
    }
  }, [state.legsDataChanged, state.legs, state.initialFormValues.legs]);

  useEffect(() => {
    if(state.invoicesDataChanged) {
      setUnsavedChanges({
        field: "invoicesDetails",
        value: !isEqual(state.invoiceDetails, state.initialFormValues.invoiceDetails)
      });

      dispatch({
        type: "fieldChanged",
        field: "invoicesDataChanged",
        value: false,
      });
    }
  }, [state.invoicesDataChanged, state.invoiceDetails, state.initialFormValues.invoiceDetails]);

  useEffect(() => {
    if (state.paymentDataChanged) {
      setUnsavedChanges({
        field: "paymentDetails",
        value: !isEqual(state.paymentDetails, state.initialFormValues.paymentDetails)
      });

      dispatch({
        type: "fieldChanged",
        field: "paymentDataChanged",
        value: false,
      })
    }
  }, [state.paymentDataChanged, state.paymentDetails, state.initialFormValues.paymentDetails]);

  if (state.loading) {
    return <Loader />;
  }

  const pageTitle = invoiceId && invoiceId !== "new" ? `Edit Invoice` : `New Invoice`;

  return (
    <Fragment>
      <PageHeader
        pageTitle={pageTitle}
        breadcrumbs={[
          {link: '/trips', label: 'Trips'},
          {link: `/trips/${tripId}`, label: `Trip #${state.data?.tripTag}`},
          {label: pageTitle, isCurrentPage: true},
        ]}
        goBackLink={`/trips/${tripId}`}
      />

      <form
        id="new-invoice"
        onSubmit={handleInvoiceSubmit}
        className="base-form"
      >
        <ProspectsAutocomplete
          onCreateOption={handleClientOptionCreation}
          handleProspectChange={handleClientChange}
          prospectOptions={[]}
          parentDispatch={dispatch}
          selectedProspect={state.selectedClient}
          label="Client"
          placeholder="Select a Client"
          required
        />

        <div className="flex-row column-gap-1pct four-columns">
          <TextInput
            name="signerName"
            label="Signer Name"
            handleChange={handleTextInputChange}
            value={state.signerName}
          ></TextInput>

          <TextInput
            name="signerEmail"
            label="Signer Email"
            handleChange={handleTextInputChange}
            value={state.signerEmail}
            type={`email`}
          ></TextInput>

          <TextInput
            name="companyName"
            label="On Behalf Of"
            handleChange={handleTextInputChange}
            value={state.companyName}
            className={"companyName"}
          ></TextInput>

          <div className="form-group ml-0" key={`signerPhone`}>
            <label className="phoneInputLabel" htmlFor="phone">
              Phone Number
            </label>

            <PhoneInput
              placeholder="Phone Number"
              value={state?.signerPhone}
              name={"signerPhone"}
              onChange={(value) => {
                setUnsavedChanges({
                  field: "signerPhone",
                  value: value !== state.initialFormValues.signerPhone,
                });

                dispatch({
                  type: "fieldChanged",
                  field: "signerPhone",
                  value: value,
                });
              }}
              onCountryChange={(value) => {
                dispatch({
                  type: "fieldChanged",
                  field: "signerPhoneCountry",
                  value: value || "",
                });
              }}
              defaultCountry={state?.signerPhoneCountry}
              className="mb-0 base-phone-input"
            />
          </div>
        </div>

        <div className="flight-details-container">
          <h5>Trip Summary</h5>
          {state?.legs?.length > 0 &&
            state?.legs?.map((leg, index) => (
              <div className="flight-details-block" key={index}>
                <div className="flex-row column-gap-1pct">
                  <div className="datepickerContainer mb-0">
                    <label
                      htmlFor={`departureDate[${index}]`}
                      className="base-label"
                    >
                      Date <span className="required">*</span>
                    </label>

                    <Datepicker
                      value={leg.departureDate}
                      onChange={(event) => {
                        dispatch({
                          type: "changeLeg",
                          payload: {
                            legIndex: index,
                            field: "departureDate",
                            value: event.value,
                          },
                        });
                      }}
                      dataTimezone="utc"
                      displayTimezone="utc"
                      timezonePlugin={momentTimezone}
                      controls={["date"]}
                    />
                  </div>

                  <div className="airportContainer flex-grow-1 mb-0">
                    <label
                      htmlFor={`departureAirport[${index}]`}
                      className="base-label"
                    >
                      Depart <span className="required">* </span>
                      {leg.departureTimeZone ? (
                        <>({leg.departureTimeZone})</>
                      ) : null}
                    </label>

                    <AsyncSelect
                      key={`departure-${leg.departureAirport?.airportName}`}
                      name={`departureAirport[${index}]`}
                      className="departure-airport-select mb-0"
                      classNamePrefix="select"
                      cacheOptions
                      loadOptions={loadAirportOptions}
                      placeholder="Search Airports"
                      defaultValue={{
                        label: leg.departureAirport.airportName,
                        value: leg.departureAirport.id,
                      }}
                      onChange={(selectedAirport) => {
                        handleAirportChange(index, selectedAirport, 0);

                        dispatch({
                          type: "changeLeg",
                          payload: {
                            legIndex: index,
                            field: "departureAirport",
                            value: {
                              airportName: selectedAirport?.label || "",
                              id: selectedAirport?.value || "",
                              code: selectedAirport?.code || "",
                              city: selectedAirport?.city || "",
                              localCode: selectedAirport?.localCode || "",
                            },
                          },
                        });
                      }}
                    />
                  </div>
                  <div className="airportContainer flex-grow-1">
                    <label
                      htmlFor={`arrivalAirport[${index}]`}
                      className="base-label"
                    >
                      Arrive <span className="required">* </span>
                      {leg.arrivalTimeZone ? (
                        <>({leg.arrivalTimeZone})</>
                      ) : null}
                    </label>

                    <AsyncSelect
                      key={`arrival-${leg.arrivalAirport.airportName}`}
                      name={`arrivalAirport[${index}]`}
                      className="arrival-airport-select mb-0"
                      classNamePrefix="select"
                      cacheOptions
                      loadOptions={loadAirportOptions}
                      placeholder="Search Airports"
                      defaultValue={{
                        label: leg.arrivalAirport.airportName,
                        value: leg.arrivalAirport.id,
                      }}
                      onChange={(selectedAirport) => {
                        handleAirportChange(index, selectedAirport, 1);

                        dispatch({
                          type: "changeLeg",
                          payload: {
                            legIndex: index,
                            field: "arrivalAirport",
                            value: {
                              airportName: selectedAirport?.label || "",
                              id: selectedAirport?.value || "",
                              code: selectedAirport?.code || "",
                              city: selectedAirport?.city || "",
                              localCode: selectedAirport?.localCode || "",
                            },
                          },
                        });
                      }}
                    />
                  </div>
                </div>

                <div className="flex-row column-gap-1pct mt-20">
                  <TextInput
                    type={"number"}
                    name={`pax[${index}]`}
                    label="PAX"
                    handleChange={(e) => {
                      dispatch({
                        type: "changeLeg",
                        payload: {
                          legIndex: index,
                          field: "pax",
                          value: parseInt(e.target.value) || "",
                        },
                      });
                    }}
                    className="base-input"
                    containerClasses="mb-0"
                    value={leg.pax}
                    required
                  ></TextInput>

                  <AircraftAutocomplete
                    handleChange={(updatedAircraft, action) => {
                      handleAircraftChange(index, updatedAircraft, action);
                    }}
                    defaultAircraftOptions={state.aircraftOptions}
                    parentDispatch={dispatch}
                    selectedAircraft={leg.aircraft}
                    required
                    containerClasses="fb-50"
                  ></AircraftAutocomplete>

                  <TextInput
                    name={`ete[${index}]`}
                    label="ETE"
                    handleChange={(e) => {
                      dispatch({
                        type: "changeLeg",
                        payload: {
                          legIndex: index,
                          field: "ete",
                          value: e.target.value || "",
                        },
                      });
                    }}
                    handleBlur={(e) => {
                      handleETEChange(index, e.target.value);
                    }}
                    className="base-input"
                    containerClasses="flex-half mb-0"
                    value={leg.ete}
                    required
                  ></TextInput>
                </div>

                {index >= 1 && (
                  <button
                    className="deleteLeg action-button base-gunmetal-button"
                    onClick={(e) => {
                      e.preventDefault();
                      return dispatch({ type: "deleteLeg", index: index });
                    }}
                    type="button"
                  >
                    Delete Leg {index + 1}
                  </button>
                )}
              </div>
            ))}
          <button
            className="action-button base-blue-button mt-0"
            onClick={handleAddLeg}
            type="button"
          >
            Add Leg
          </button>
        </div>

        <div className="flight-details-container">
          <h5 className="d-flex align-items-center mb-1">
            Invoice Details
            <button
              className="base-button icon-button icon-button-small evo-dark-green ml-2"
              onClick={handleAddDetail}
              title="Add Detail"
              type="button"
            >
              <span className="material-icons">add_circle</span>
            </button>
          </h5>

          {state?.invoiceDetails?.length > 0 &&
            state?.invoiceDetails?.map((detail, index) => {
              const isNotesRequired = detail?.typeLabel?.notesRequired;

              return (
                <div className="flight-details-block" key={index}>
                  <div className="flex-row column-gap-1pct">
                    <div className="form-group flex-half">
                      <label htmlFor={`type[${index}]`}>
                        Type <span className="required">*</span>
                      </label>
                      <Select
                        className="type-select"
                        classNamePrefix="select"
                        options={invoiceTypesOptions}
                        placeholder="Type"
                        onChange={(selection) => {
                          dispatch({
                            type: "changeInvoiceDetailField",
                            payload: {
                              invoiceDetailIndex: index,
                              field: "typeLabel",
                              value: selection,
                            },
                          });
                        }}
                        key={`type${index}`}
                        value={detail.typeLabel}
                        defaultValue={detail.typeLabel}
                      />
                    </div>
                    <div className="form-group flex-half">
                      <label htmlFor={`amount[${index}]`}>
                        Amount <span className="required">*</span>
                      </label>
                      <NumberFormat
                        thousandSeparator={true}
                        prefix={"$"}
                        onValueChange={(event) => {
                          dispatch({
                            type: "changeInvoiceDetailField",
                            payload: {
                              invoiceDetailIndex: index,
                              field: "amount",
                              value: event.value,
                            },
                          });
                        }}
                        placeholder="Amount"
                        className="semibold-text mb-0"
                        decimalScale={2}
                        name={`amount[${index}]`}
                        value={state.invoiceDetails[index].amount}
                      />
                    </div>
                    <div className="form-group flex-one-and-half">
                      <label htmlFor={`description[${index}]`}>
                        Description{" "}
                        {isNotesRequired ? (
                          <span className="required">*</span>
                        ) : (
                          ``
                        )}
                      </label>
                      <textarea
                        id={`description${index}`}
                        placeholder="Description"
                        onChange={(e) => {
                          dispatch({
                            type: "changeInvoiceDetailField",
                            payload: {
                              invoiceDetailIndex: index,
                              field: "description",
                              value: e.target.value,
                            },
                          });
                        }}
                        key={`description[${index}]`}
                        value={state.invoiceDetails[index].description || ""}
                        required={isNotesRequired}
                        rows={1}
                      />
                    </div>
                    <div className="payment-detail-actions">
                      <button
                        className="base-button icon-button btn-danger"
                        title="Delete Detail"
                        onClick={(e) => {
                          e.preventDefault();
                          return dispatch({
                            type: "deleteInvoiceDetail",
                            index: index,
                          });
                        }}
                        type="button"
                      >
                        <span className="material-icons">delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        <div className="flight-details-container payment-details-container">
          <h5 className="d-flex align-items-center mb-1">
            Payment Details
            {!state.paymentsLocked && (
              <button
                className="base-button icon-button icon-button-small evo-dark-green ml-2"
                onClick={handleAddPaymentDetail}
                title="Add Payment Detail"
                type="button"
              >
                <span className="material-icons">add_circle</span>
              </button>
            )}
          </h5>

          {state.paymentsLocked && (
            <p className="mb-20">
              Payments are tied to this Trip's Client Payment and Incidental
              Charges records and cannot be edited. They always update to
              match the latest records. Save the invoice to apply these
              payments and deliver the updated invoice to the client's app.
            </p>
          )}

          {state.paymentDetails?.length > 0 &&
            state.paymentDetails.map((detail, index) => {
              const badge = state.paymentsLocked ? detail.badge : null;
              const isDeleted = badge === "deleted";
              const badgeMeta = {
                new: { icon: "add_circle", title: "New" },
                modified: { icon: "edit_note", title: "Modified" },
                deleted: { icon: "delete_outline", title: "Deleted" },
              }[badge];

              return (
                <div
                  className={`flight-details-block payment-detail-row${
                    state.paymentsLocked ? " payment-detail-row--locked" : ""
                  }${isDeleted ? " payment-detail-row--deleted" : ""}`}
                  key={detail.clientPaymentId || detail.incidentalChargeId || index}
                >
                  <div className="flex-row column-gap-1pct payment-detail-fields">
                    {state.paymentsLocked && (
                      <div
                        className={`payment-detail-badge payment-detail-badge--${
                          badge || "none"
                        }`}
                      >
                        {badgeMeta && (
                          <span className="material-icons" title={badgeMeta.title}>
                            {badgeMeta.icon}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="datepickerContainer mb-0 form-group payment-field-date">
                      <label
                        htmlFor={`departureDate[${index}]`}
                        className="base-label"
                      >
                        Date{" "}
                        {!state.paymentsLocked && (
                          <span className="required">*</span>
                        )}
                      </label>

                      {state.paymentsLocked ? (
                        <div className="payment-detail-readonly">
                          {moment(detail.date).isValid()
                            ? moment(detail.date).format("MM/DD/YYYY")
                            : ""}
                        </div>
                      ) : (
                        <Datepicker
                          value={detail.date}
                          onChange={(event) => {
                            dispatch({
                              type: "changePaymentDetailField",
                              payload: {
                                paymentDetailIndex: index,
                                field: "date",
                                value: event.value,
                              },
                            });
                          }}
                          dataTimezone="utc"
                          displayTimezone="local"
                          timezonePlugin={momentTimezone}
                          controls={["date"]}
                        />
                      )}
                    </div>

                    <div className="form-group payment-field-type">
                      <label htmlFor={`paymentDetailType[${index}]`}>
                        Type{" "}
                        {!state.paymentsLocked && (
                          <span className="required">*</span>
                        )}
                      </label>
                      {state.paymentsLocked ? (
                        <div className="payment-detail-readonly">
                          {detail.typeLabel?.label || ""}
                        </div>
                      ) : (
                        <Select
                          className="type-select"
                          classNamePrefix="select"
                          options={INVOICE_PAYMENT_TYPE_OPTIONS}
                          placeholder="Type"
                          onChange={(selection) => {
                            dispatch({
                              type: "changePaymentDetailField",
                              payload: {
                                paymentDetailIndex: index,
                                field: "typeLabel",
                                value: selection,
                              },
                            });
                          }}
                          defaultValue={detail.typeLabel}
                          value={detail.typeLabel}
                          key={`paymentDetailType${index}`}
                        />
                      )}
                    </div>

                    <div className="form-group payment-field-amount">
                      <label htmlFor={`amount[${index}]`}>
                        Amount{" "}
                        {!state.paymentsLocked && (
                          <span className="required">*</span>
                        )}
                      </label>
                      {state.paymentsLocked ? (
                        <div className="payment-detail-readonly">
                          {currencyFormatter.format(detail.amount || 0)}
                        </div>
                      ) : (
                        <NumberFormat
                          value={detail.amount}
                          thousandSeparator={true}
                          prefix={"$"}
                          onValueChange={(event) => {
                            dispatch({
                              type: "changePaymentDetailField",
                              payload: {
                                paymentDetailIndex: index,
                                field: "amount",
                                value: event.value,
                              },
                            });
                          }}
                          placeholder="Amount"
                          className="semibold-text mb-0"
                          decimalScale={2}
                          name={`amount[${index}]`}
                        />
                      )}
                    </div>

                    <div className="form-group payment-field-notes">
                      <label htmlFor={`paymentDetailsNotes[${index}]`}>
                        Notes
                        {detail.type === "other" && (
                          <span className="required">*</span>
                        )}
                      </label>
                      <textarea
                        id={`paymentDetailsNotes${index}`}
                        placeholder="Notes"
                        value={detail.notes || ""}
                        disabled={isDeleted}
                        onChange={(event) => {
                          dispatch({
                            type: "changePaymentDetailField",
                            payload: {
                              paymentDetailIndex: index,
                              field: "notes",
                              value: event.target.value,
                            },
                          });
                        }}
                        key={`paymentDetailsNotes[${index}]`}
                        rows={1}
                      ></textarea>
                    </div>

                    {!state.paymentsLocked && (
                      <div className="payment-detail-actions">
                        <button
                          className="base-button icon-button btn-danger"
                          title="Delete Payment Detail"
                          onClick={(e) => {
                            e.preventDefault();
                            return dispatch({
                              type: "deletePaymentDetail",
                              index: index,
                            });
                          }}
                          type="button"
                        >
                          <span className="material-icons">delete</span>
                        </button>
                      </div>
                    )}
                  </div>

                  {isDeleted && (
                    <span className="payment-detail-deleted-label">Deleted</span>
                  )}
                </div>
              );
            })}
        </div>

        <div className="notes-container">
          <h5>Invoice Notes</h5>

          <Editor
            editorState={state.invoiceNotesEditor}
            wrapperClassName="header-editor-wrapper"
            editorClassName="notes-editor"
            onEditorStateChange={(editorState) => {
              setUnsavedChanges({
                field: 'invoiceNotesEditor',
                value:
                  editorState?.getCurrentContent().getPlainText()
                  !== state.initialFormValues.invoiceNotesEditor?.getCurrentContent().getPlainText(),
              });

              dispatch({
                type: "fieldChanged",
                field: "invoiceNotesEditor",
                value: editorState,
              })
            }}
          />
        </div>

        <LoadingButton
          className="mt-0 mb-20 base-button evo-blue"
          onClick={handleInvoiceSubmit}
          type="submit"
          id="preview"
          disabled={state.isFormLoading}
          isLoading={state.isSaveAndPreviewLoading}
        >
          Save and Preview
        </LoadingButton>

        <LoadingButton
          className="mt-0 mb-20 ml-20 base-button evo-gunmetal"
          onClick={handleInvoiceSubmit}
          type="submit"
          id="view-trip"
          disabled={state.isFormLoading}
          isLoading={state.isSaveAndViewTripLoading}
        >
          Save and View Trip
        </LoadingButton>
      </form>

      <StateDisplay user={user} data={state}></StateDisplay>

      <UnsavedChangesWarningModal isChanged={!!Object.keys(state.unsavedChanges).length}/>
    </Fragment>
  );
};

export default NewInvoiceContent;
